'use client';
import cn from 'classnames';
import { displayTierName } from '@/util/product_client_safe';
import Image from 'next/image';
import { Tooltip } from 'react-tooltip';
import CircleQuestionMark from 'public/img/icon/circle-questionmark.svg';
import Close from 'public/img/icon/close.svg';

import ChevronDown from 'public/img/icon/chevron-down-black.svg';
import './SignUpSummaryBar.style.scss';

import Text from '@/components/common/Text/Text';
import SignUpSummaryModal from '@/components/modal/SignUpSummaryModal/SignUpSummaryModal';
import { SignupState as RegistrationValues } from '@/types/Signup';
import { useModal } from '@/components/hook/modal';
import { RawEPCCCartResponse } from '@/types/Cart';
import { InsuranceProduct, SubscriptionProduct } from '@/types/Subscription';
import { capitalize } from '@/util/misc';
import { ReferralCopyProps } from '../SignUpApp/SignUpApp';

type SignUpSummaryBarProps = {
  className?: string;
  selectedPlan: any;
  subs: SubscriptionProduct[];
  insuranceData: InsuranceProduct[];
  user: RegistrationValues;
  previewCart?: RawEPCCCartResponse;
  referralCopy?: ReferralCopyProps;
};

function SignUpSummaryBar(props: SignUpSummaryBarProps) {
  const {
    className = '',
    selectedPlan,
    subs,
    insuranceData,
    user,
    previewCart,
    referralCopy
  } = props;
  const modalTools = useModal();

  return (
    <div className={cn('SignUpSummaryBar', className)}>
      <div className='SignUpSummaryBar-mobile'>
        <button
          className='SignUpSummaryBar-btn cursor'
          onClick={() => {
            modalTools.showModal(
              <SignUpSummaryModal
                subs={subs}
                insuranceData={insuranceData}
                onClose={() => {
                  modalTools.closeModal();
                }}
                user={user}
                previewCart={previewCart}
              />,
              {
                isFullScreen: true,
                anchorPoint: 'bottom'
              }
            );
          }}
        >
          <Text variant='title-xxs-700-upper'>Membership summary</Text>
          <Image
            className='SignUpSummaryBar-chevron'
            src={ChevronDown}
            height={16}
            width={16}
            alt=''
          />
        </button>
      </div>

      <div className='SignUpSummaryBar-desktop'>
        <div className='SignUpSummaryBar-info'>
          <Text
            className='SignUpSummaryBar-title'
            variant={'title-xs'}
            uppercase
          >
            Summary
          </Text>
        </div>
        <div className='SignUpSummaryBar-info'>
          <p>
            <span className='SignUpSummaryBar-label'>Tier: </span>
            {user.addon_subscription === 'premier' ? (
              <>
                {displayTierName(selectedPlan?.name ?? '')},{' '}
                {capitalize(user.addon_subscription)} add-on
              </>
            ) : (
              <>{displayTierName(selectedPlan?.name ?? '')}</>
            )}
          </p>
          <p>
            <span className='SignUpSummaryBar-label'>Coverage: </span>
            {capitalize(user.insurance_level)}
          </p>
        </div>
        <div className='SignUpSummaryBar-info'>
          <div>
            <p>
              <span className='SignUpSummaryBar-label'>Referral: </span>
              {user.referralCode ? <>{user.referralCode}</> : <>None</>}
            </p>
            <Text variant={'text-s'}>{referralCopy?.copy}</Text>
          </div>
        </div>
        <div className='SignUpSummaryBar-info SignUpSummaryBar-info--pricing'>
          <div>
            <span className='SignUpSummaryBar-label'>Monthly: </span>
            {previewCart?.data?.meta.display_price.without_tax.formatted}
            <Text variant={'text-s'}>Billed after acceptance</Text>
          </div>
          <div>
            <span className='SignUpSummaryBar-label SignUpSummaryBar-label--total'>
              Due Now: <span className='SignUpSummaryBar-value'>$0</span>
            </span>
            <button className='SignUpSummaryBar-tooltip-anchor cursor'>
              <Image
                src={CircleQuestionMark}
                alt='Info'
                width={14}
                height={14}
                style={{ marginTop: '-1px', marginLeft: '3px' }}
              />
            </button>
            <Text variant={'text-s'}>No charge until acceptance</Text>
          </div>
          <Tooltip
            anchorSelect='.SignUpSummaryBar-tooltip-anchor'
            className='SignUpSummaryBar-tooltip cursor'
            classNameArrow='SignUpSummaryBar-tooltip-arrow'
            place='right'
          >
            <Image
              className='tooltip-close'
              src={Close}
              alt=''
            />
            <div className='SignUpSummaryBar-tooltip-content'>
              <Text
                variant='title-xxs-500'
                uppercase
              >
                When will I be charged?
              </Text>
              <Text variant={'text-s'}>
                Your billing cycle will begin once your membership application
                has been reviewed by our team and accepted.
              </Text>
            </div>
          </Tooltip>
        </div>
        <div className='SignUpSummaryBar-button-wp'>
          <button
            className='SignUpSummaryBar-edit-button cursor'
            onClick={() => {
              modalTools.showModal(
                <SignUpSummaryModal
                  subs={subs}
                  insuranceData={insuranceData}
                  onClose={() => {
                    modalTools.closeModal();
                  }}
                  user={user}
                  previewCart={previewCart}
                />,
                {
                  isFullScreen: true,
                  anchorPoint: 'bottom'
                }
              );
            }}
          >
            Edit
          </button>
        </div>
      </div>
    </div>
  );
}

export default SignUpSummaryBar;
